import React, { useEffect } from "react";
import {
  Box,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
} from "@mui/material";

const LAST_UPDATED = "17 October 2026";

/* Review all conditional disclosures against production behavior before publishing. */
const OPERATOR = "AIORBIT APPS (OPC) PRIVATE LIMITED";
const PRIVACY_EMAIL = "admin@aiorbitapps.com";
const BUSINESS_ADDRESS = "67-21-17 Lalbahadur Nagar, Kakinada, Gandhinagar East Godavari Andhra Pradesh 533004";

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mb: 3.5 }}>
    <Typography
      component="h2"
      sx={{ fontSize: { xs: "1.15rem", md: "1.3rem" }, fontWeight: 700, mb: 1 }}
    >
      {title}
    </Typography>
    <Box sx={{ color: "text.secondary", lineHeight: 1.75 }}>{children}</Box>
  </Box>
);

const P = ({ children }) => (
  <Typography component="p" sx={{ mb: 1.25, lineHeight: 1.75 }}>
    {children}
  </Typography>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | UniFLOO";
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa", py: { xs: 2, md: 5 } }}>
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, bgcolor: "background.paper" }}>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: "1.8rem", md: "2.35rem" }, fontWeight: 800 }}
            >
              UniFLOO Privacy Policy
            </Typography>
            <Typography sx={{ mt: 1, color: "text.secondary" }}>
              Last updated: {LAST_UPDATED}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 3, md: 4 }, bgcolor: "background.paper" }}>
            <Section title="1. About this policy">
              <P>
                This Privacy Policy explains how UniFLOO, operated by {OPERATOR},
                handles personal information when individuals use UniFLOO websites,
                applications, institutional services, and related communication features.
                In some deployments, a college or other educational institution determines
                why and how student, employee, or institutional information is processed.
              </P>
            </Section>

            <Section title="2. Information we may process">
              <P>
                Depending on the UniFLOO services enabled by an institution, information may
                include account and identity details; student or employee identifiers; contact
                details; academic, attendance, application, hostel, transport, fee, assignment,
                career, and administrative records; documents or content submitted by users;
                device, authentication, security, audit, and technical information; and
                communications sent through enabled notification or messaging channels.
              </P>
              <P>
                The exact information processed depends on the institution's configuration,
                the user's role, and the features the user chooses to use.
              </P>
            </Section>

            <Section title="3. How information is used">
              <P>
                Information may be used to provide and administer UniFLOO services, authenticate
                users, support educational and institutional workflows, process user requests,
                deliver notifications, maintain security and audit records, troubleshoot and
                improve services, and comply with applicable legal obligations.
              </P>
            </Section>

            <Section title="4. WhatsApp and other communications">
              <P>
                Where an institution enables WhatsApp, email, SMS, push notifications, Telegram,
                or another communication provider, relevant contact details and message content
                may be processed to send or receive the requested communications. WhatsApp
                communications use Meta's WhatsApp services and may also be subject to Meta's
                applicable terms and privacy practices.
              </P>
              <P>
                UniFLOO does not use the existence of a messaging integration as permission to
                send unrelated communications. Institutions are responsible for configuring
                communication purposes and obtaining any consent required by applicable law.
              </P>
            </Section>

            <Section title="5. Institutions and service providers">
              <P>
                UniFLOO may process information on behalf of participating educational
                institutions. Information may also be handled by infrastructure, hosting,
                messaging, payment, authentication, analytics, support, or other service
                providers where needed to deliver configured functionality. Providers should
                receive only information reasonably necessary for the relevant service.
              </P>
            </Section>

            <Section title="6. Payments">
              <P>
                If payment functionality is enabled, payment transactions may be processed by
                third-party payment providers. UniFLOO may retain transaction references,
                amounts, statuses, reconciliation information, and related records needed to
                administer the transaction, while payment providers process payment credentials
                according to their own terms and privacy practices.
              </P>
            </Section>

            <Section title="7. Security">
              <P>
                We use administrative, technical, and organizational safeguards intended to
                protect information against unauthorized access, alteration, disclosure, or
                destruction. No internet-connected system can guarantee absolute security.
              </P>
            </Section>

            <Section title="8. Data retention">
              <P>
                Information is retained for as long as reasonably necessary for the purposes for
                which it was collected, institutional recordkeeping requirements, security,
                dispute resolution, contractual requirements, and applicable law. Retention can
                vary by institution and type of record.
              </P>
            </Section>

            <Section title="9. Access, correction and deletion requests">
              <P>
                Individuals may contact their educational institution for requests concerning
                institution-controlled records. For requests concerning UniFLOO directly,
                including applicable access, correction, deletion, or privacy questions, contact
                us using the details below. Requests may require identity verification, and some
                information may need to be retained where required or permitted by law.
              </P>
            </Section>

            <Section title="10. Children and student users">
              <P>
                UniFLOO may be used by educational institutions whose students include minors.
                Institutions are responsible for using UniFLOO consistently with applicable
                education, child-privacy, consent, and guardian requirements. UniFLOO services
                should not be used to collect information from children outside an authorized
                institutional context where legally required permissions have not been obtained.
              </P>
            </Section>

            <Section title="11. International processing">
              <P>
                Some service providers may process information in locations different from the
                user's location. Where applicable, appropriate contractual, organizational, or
                legal safeguards should be used for cross-border processing.
              </P>
            </Section>

            <Section title="12. Changes to this policy">
              <P>
                This policy may be updated when UniFLOO's services or legal requirements change.
                The current version will be published on this page with an updated effective date.
              </P>
            </Section>

            <Section title="13. Contact">
              <P>
                Operator: {OPERATOR}<br />
                Privacy email: {PRIVACY_EMAIL}<br />
                Address: {BUSINESS_ADDRESS}
              </P>
              <P>
                If your account is provided by a college or other institution, you may also
                contact that institution's administration or privacy contact regarding records
                it controls.
              </P>
            </Section>

            <Section title="14. Mobile application permissions and device features">
              <P>
                Depending on the features used, UniFLOO may request access to device
                capabilities. Camera access may be used for QR code scanning, profile
                photographs, document capture, and classroom features. Microphone access
                may be used for audio participation in Live Classroom sessions. Photo
                library access may be used to select photographs, documents, or other
                images for upload.
              </P>
              <P>
                Where enabled, notifications may deliver academic, administrative,
                attendance, fee, security, and institutional updates. Supported screen
                sharing features may allow a user to choose to share their screen
                during a classroom session. Device permissions are subject to operating
                system controls and may be changed in device settings. Content that a
                user chooses to capture, upload, or share may be processed to provide
                the requested feature.
              </P>
            </Section>

            <Section title="15. Authentication and third-party sign-in">
              <P>
                Depending on the account and enabled features, UniFLOO may support
                passwords, one-time verification codes, passkeys, Sign in with Apple,
                Google sign-in, LinkedIn sign-in, or other configured authentication
                methods. Authentication providers may supply account identifiers,
                names, email addresses, or related sign-in information according to
                the permissions granted and their own privacy practices.
              </P>
              <P>
                Authentication-related information may be used to verify identity,
                maintain sessions, prevent unauthorized access, and secure accounts.
              </P>
            </Section>

            <Section title="16. Account deletion and institutional records">
              <P>
                Users may request deletion of their UniFLOO accounts and associated
                personal information by contacting their institution, where it
                controls the account, or by emailing {PRIVACY_EMAIL}. Where an in-app
                account deletion option is provided, users may initiate a request
                through that option. Requests may require identity verification.
              </P>
              <P>
                Account deletion and deletion of institution-controlled records are
                not always the same process. Academic, financial, audit, and other
                records may be retained where required or permitted by law or where
                an institution has applicable recordkeeping obligations. We will
                explain applicable limitations when responding to a request.
              </P>
            </Section>

            <Section title="17. Analytics, advertising, and tracking">
              <P>
                UniFLOO may process technical diagnostics, application performance
                information, and usage information to operate, protect, troubleshoot,
                and improve its services. If analytics or diagnostic providers are
                enabled, they may process information needed for those functions,
                subject to their applicable terms and privacy practices.
              </P>
              <P>
                Any advertising or cross-app tracking practices, if introduced or
                enabled, must be disclosed as applicable and handled in accordance
                with relevant consent and platform requirements. The presence of
                operational analytics alone does not necessarily mean that users are
                tracked for advertising purposes.
              </P>
            </Section>

            <Section title="18. Photos, uploaded content, and location information">
              <P>
                UniFLOO may process photographs, resumes, assignments, documents,
                classroom materials, feedback, messages, and other content that users
                or institutions submit, as needed to provide the relevant feature.
                Access to this content may depend on the institution, user role,
                sharing choices, and configured permissions.
              </P>
              <P>
                If a feature requests location information, its purpose and any
                applicable device permission will depend on that feature. UniFLOO
                should not be understood to collect precise location solely because
                it provides campus, hostel, or transport management functionality.
              </P>
            </Section>

            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary">
              <Link href="/" underline="hover">Return to UniFLOO</Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
